/**
 * Offline play for every game: the real game engine and bots run right here in the browser, at a real table
 * (entry fee, seats, prizes and bot strength come from the table the catalog cache holds), and the result is
 * fed through the same store actions the server uses, so the normal table screen works unchanged.
 *
 * Nothing is paid locally for good. The finished game's replay log is queued (see claims.ts) and the server
 * re-runs it against the real table rules, then applies the coins, XP, stats and rating once we are online.
 * Until then the shown balance moves provisionally (stake off at the start, result added at the end).
 */
import {
  OFFLINE_HUMAN_SEAT,
  OfflineGame,
  computeMatchPayouts,
  tablePrizeRules,
  type EngineEvent,
  type GameType,
  type OfflineTableSpec,
} from '@cardhub/engine';
import type { GameResultPayload, SeatPublic, TableConfigDto, TableInfo, TurnInfo } from '@cardhub/protocol';
import { realtime, useSession } from '../store/session';
import { applyGameState, applyResult, applySnapshot, useGame } from '../store/game';
import { toast } from '../store/ui';
import { uuid } from '../lib/device';
import { beginClaim, cancelClaim, completeClaim, flushClaims } from './claims';

export const OFFLINE_TABLE_ID = 'offline-local';
export const isOfflineTable = (id: string | null | undefined): boolean => id === OFFLINE_TABLE_ID;

/** True when there is no way to reach the server right now, so games should be played locally. */
export function isOffline(): boolean {
  return !navigator.onLine || useSession.getState().offlineStart;
}

const BOTS = [
  { username: 'Aarav', avatar: 'avatar_04' },
  { username: 'Maya', avatar: 'avatar_10' },
  { username: 'Rohan', avatar: 'avatar_17' },
  { username: 'Priya', avatar: 'avatar_02' },
  { username: 'Kabir', avatar: 'avatar_13' },
  { username: 'Zoya', avatar: 'avatar_07' },
  { username: 'Dev', avatar: 'avatar_21' },
  { username: 'Anika', avatar: 'avatar_15' },
];

interface Current {
  game: OfflineGame;
  table: TableConfigDto;
  matchId: string;
  /** Free (0-entry) tables are never claimed: nothing to settle. */
  claims: boolean;
  userId: string | null;
  seq: number;
  timer: ReturnType<typeof setTimeout> | null;
  done: boolean;
}

let cur: Current | null = null;
/** The table last played, for "play again". */
let lastTable: TableConfigDto | null = null;

function clearTimer(): void {
  if (cur?.timer) clearTimeout(cur.timer);
  if (cur) cur.timer = null;
}

/** Stops the local game without settling it (used when the screen is torn down after it already ended). */
export function stopOfflineGame(): void {
  clearTimer();
  cur = null;
}

const turnMs = (c: Current): number => c.table.turnTimerSec * 1000;

function turnInfo(c: Current): TurnInfo {
  const actor = c.game.actor;
  const mine = actor === OFFLINE_HUMAN_SEAT;
  // The countdown subtracts the server clock offset, so pre-apply it to stay correct on the device clock.
  return { seats: actor === null ? [] : [actor], deadline: mine ? Date.now() - realtime.serverOffsetMs + turnMs(c) : null, turnMs: turnMs(c) };
}

function seatList(c: Current): SeatPublic[] {
  const me = useSession.getState().user;
  const chips = c.game.kind === 'session' ? c.game.chips() : {};
  return c.game.seatMembers().map((m, i) => {
    const extra = chips[i] !== undefined ? { chips: chips[i] } : {};
    if (!m) return { seat: i, user: null, isBot: false, connected: false, botControlled: false };
    if (!m.isBot) return { seat: i, user: me ? { id: me.id, username: me.username, avatar: me.avatar, level: me.level } : null, isBot: false, connected: true, botControlled: false, ...extra };
    const b = BOTS[(m.botNo - 1) % BOTS.length]!;
    return { seat: i, user: { id: `offline-bot-${m.botNo}`, username: b.username, avatar: b.avatar, level: 1, isBot: true }, isBot: true, connected: true, botControlled: false, ...extra };
  });
}

function specOf(t: TableConfigDto): OfflineTableSpec {
  return { gameType: t.gameType as GameType, rules: t.rules, entryFee: t.entryFee, minPlayers: t.minPlayers, maxPlayers: t.maxPlayers, difficulty: t.botDifficulty ?? 'NORMAL' };
}

/** Starts a fresh offline game at a table and shows it on the table screen. Returns false if it cannot start. */
export function startOfflineTable(table: TableConfigDto): boolean {
  const g = useGame.getState();
  // An online table we can no longer reach (the connection dropped) is not a reason to refuse a local game.
  if (g.table && !isOfflineTable(g.table.tableId) && !g.result && !g.closed && !isOffline() && useSession.getState().ws === 'open') {
    toast('You are still in a game. Finish it or leave the table first.', 'info');
    return false;
  }
  const session = useSession.getState();
  if (table.entryFee > 0 && session.balance < Math.max(table.entryFee, table.minBalance)) {
    toast('Not enough coins for this table.', 'error');
    return false;
  }
  stopOfflineGame();
  useGame.getState().reset();
  let game: OfflineGame;
  try {
    game = new OfflineGame(specOf(table));
  } catch (e) {
    toast(e instanceof Error ? `Could not start this table offline (${e.message})` : 'Could not start this table offline', 'error');
    return false;
  }
  const userId = session.user?.id ?? null;
  const c: Current = { game, table, matchId: uuid(), claims: table.entryFee > 0 && userId !== null, userId, seq: 1, timer: null, done: false };
  cur = c;
  lastTable = table;
  if (c.claims) beginClaim({ id: c.matchId, userId: userId!, tableConfigId: table.id, stake: table.entryFee });
  const info: TableInfo = {
    tableId: OFFLINE_TABLE_ID,
    matchId: c.matchId,
    gameType: table.gameType,
    tableConfigId: table.id,
    name: table.name,
    entryFee: table.entryFee,
    status: 'IN_PROGRESS',
    privacy: 'PRIVATE',
    roomCode: null,
    maxPlayers: table.maxPlayers,
  };
  applySnapshot({ table: info, seq: c.seq, mySeat: OFFLINE_HUMAN_SEAT, seats: seatList(c), view: game.view(), legal: game.legal(), turn: turnInfo(c), serverTime: Date.now() });
  schedule();
  return true;
}

/** Plays the table that was played last once more (the result screen's "Play again"). */
export function restartOfflineTable(): boolean {
  const t = cur?.table ?? lastTable;
  return t ? startOfflineTable(t) : false;
}

function publish(c: Current, events: EngineEvent[]): void {
  const g = c.game;
  if (g.kind === 'session') sessionHands(c);
  if (g.finished) return;
  c.seq++;
  applyGameState({ tableId: OFFLINE_TABLE_ID, seq: c.seq, events, view: g.view(), legal: g.legal(), turn: turnInfo(c), serverTime: Date.now() });
  if (g.kind === 'session') useGame.setState({ seats: seatList(c) });
}

/** Hands of a session game that just ended: show the player's chip change like a live table does. */
function sessionHands(c: Current): void {
  const user = useSession.getState().user;
  for (const h of c.game.takeHands()) {
    const delta = h.deltas[OFFLINE_HUMAN_SEAT];
    if (delta === undefined) continue;
    const seats = seatList(c);
    const won = delta > 0;
    const result: GameResultPayload = {
      tableId: OFFLINE_TABLE_ID,
      matchId: c.matchId,
      gameType: c.table.gameType,
      standings: Object.entries(h.deltas).map(([seat, d]) => ({ seat: Number(seat), place: d > 0 ? 1 : 2, score: d, user: seats[Number(seat)]?.user ?? null, isBot: seats[Number(seat)]?.isBot ?? false })),
      me: { seat: OFFLINE_HUMAN_SEAT, place: won ? 1 : 2, coinsDelta: delta, xpGained: 0, level: user?.level ?? 1, leveledUp: false, ratingDelta: null, rating: null },
    };
    applyResult(result);
  }
}

/** Handles a move from the player's UI. */
export function offlineAct(action: Record<string, unknown>): boolean {
  const c = cur;
  if (!c || c.done || c.game.actor !== OFFLINE_HUMAN_SEAT) return false;
  let events: EngineEvent[];
  try {
    events = c.game.act(OFFLINE_HUMAN_SEAT, action);
  } catch (e) {
    toast(e instanceof Error ? e.message : 'Not allowed', 'error');
    return false;
  }
  clearTimer();
  publish(c, events);
  schedule();
  return true;
}

function schedule(): void {
  const c = cur;
  if (!c || c.done) return;
  clearTimer();
  const g = c.game;
  if (g.finished) return finish(c);
  const actor = g.actor;
  if (actor === OFFLINE_HUMAN_SEAT) {
    c.timer = setTimeout(() => {
      const events = g.act(OFFLINE_HUMAN_SEAT, g.timeoutAction());
      publish(c, [...events, { type: 'turnTimeout', seat: OFFLINE_HUMAN_SEAT }]);
      schedule();
    }, turnMs(c));
  } else if (actor !== null) {
    c.timer = setTimeout(() => {
      publish(c, g.botMove(actor));
      schedule();
    }, Math.min(g.thinkMs(), turnMs(c) / 2));
  } else if (g.systemStep) {
    c.timer = setTimeout(() => {
      publish(c, g.runSystemStep());
      schedule();
    }, g.systemStep.delayMs);
  }
}

/** The provisional coin result of a finished game (the server recomputes it, and applies its daily cap, on claim). */
function provisionalNet(c: Current): number {
  const g = c.game;
  if (g.kind === 'session') return g.cashoutChips - g.buyIn;
  const { payoutPercents, rakePercent } = tablePrizeRules(c.table.rules);
  const payout = g.abandoned ? 0 : (computeMatchPayouts(c.table.entryFee * c.table.maxPlayers, g.standings(), payoutPercents, rakePercent).payouts.get(OFFLINE_HUMAN_SEAT) ?? 0);
  return payout - c.table.entryFee;
}

function settleLocally(c: Current): number {
  c.done = true;
  clearTimer();
  const net = provisionalNet(c);
  if (c.claims) {
    completeClaim(c.matchId, { log: c.game.log, net, abandoned: c.game.abandoned });
    void flushClaims();
  }
  return net;
}

function finish(c: Current): void {
  if (c.done) return;
  const g = c.game;
  if (g.kind === 'session') {
    sessionHands(c);
    const chips = g.cashoutChips;
    settleLocally(c);
    useGame.setState({ closed: { tableId: OFFLINE_TABLE_ID, reason: chips < 1 ? 'insufficient_chips' : g.logFull ? 'session_limit' : 'left', chips } });
    return;
  }
  const user = useSession.getState().user;
  const seats = seatList(c);
  const standings = g.standings();
  const place = standings.find((s) => s.seat === OFFLINE_HUMAN_SEAT)?.place ?? standings.length;
  const net = settleLocally(c);
  const result: GameResultPayload = {
    tableId: OFFLINE_TABLE_ID,
    matchId: c.matchId,
    gameType: c.table.gameType,
    standings: standings.map((s) => ({ seat: s.seat, place: s.place, score: s.score, user: seats[s.seat]?.user ?? null, isBot: seats[s.seat]?.isBot ?? false })),
    // Provisional: the server confirms the amount (and its daily cap), the XP and the rating when the game is claimed.
    me: { seat: OFFLINE_HUMAN_SEAT, place, coinsDelta: net, xpGained: 0, level: user?.level ?? 1, leveledUp: false, ratingDelta: null, rating: null },
  };
  applyResult(result);
}

/**
 * The player leaves the table. A session game cashes out the chips they have; a match that is still running is
 * finished by the bots for them (so walking away costs the stake, as it does online).
 */
export function leaveOfflineGame(): void {
  const c = cur;
  if (!c) return;
  if (!c.done) {
    if (c.game.kind === 'session') c.game.leave();
    else c.game.forfeit();
    settleLocally(c);
  }
  stopOfflineGame();
}

/** For a game that never got going (an error while starting): give back the stake. */
export function abortOfflineStart(): void {
  if (cur?.claims && !cur.done) cancelClaim(cur.matchId);
  stopOfflineGame();
}
